import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import {
  HdsIcon,
  HdsTextBody,
  HdsTextDisplay,
  HdsButtonSet,
  HdsButton,
  HdsAccordion,
  HdsCodeBlock,
} from '@hashicorp/design-system-components/components';
import { Typography, Flex } from 'core/components';
import { boxMargin } from 'core/modifiers';
import { setRedirectTarget } from 'hcp/utils/redirect';
import GlobalErrorFrame from 'hcp/components/global-error-frame';
import optionForGlobalError from 'hcp/helpers/option-for-global-error';

/**
 *
 * `PageGlobalError` is used to display an invitation error.
 *
 *
 * ```
 * <Page::GlobalError @error={{error}} @variant={{errorType}} />
 * ```
 *
 * @class PageAcceptInvitationError
 *
 */
/**
 *
 * `error` containts the http error from a failed invitation acceptance call.
 * @argument error;
 * @type {Object}
 *
 */
/**
 *
 * `variant` changes the state of which error messaging is rendered.
 * @argument variant;
 * @type {String}
 *
 */
export default class PageGlobalError extends Component {
  @service session;
  get errorText() {
    if (!this.args.error) {
      return '';
    }
    let { message, stack } = this.args.error;
    return `${message}

${stack && JSON.stringify(stack, null, 2).replace(/\\n/g, '\n')}
    `;
  }

  restartSession = () => {
    setRedirectTarget('/sign-up');
    this.session.invalidate();
  };

  <template>
    <GlobalErrorFrame
      class="global-error"
      data-test-page-global-error-container
    >
      <:content>
        {{#let (optionForGlobalError @variant) as |option|}}
          <header>
            {{#if option.subtitle}}
              <Flex
                @alignItems="center"
                @direction="row"
                @gap="2xs"
                {{boxMargin "0 0 sm 0"}}
              >
                <HdsIcon
                  @color="var(--token-color-foreground-critical-on-surface)"
                  @name="x-square-fill"
                  @isInline={{true}}
                />
                <HdsTextBody
                  @color="critical-on-surface"
                  @size="200"
                  @tag="h2"
                  data-test-page-global-error-subtitle
                >
                  {{t option.subtitle}}
                </HdsTextBody>
              </Flex>
            {{/if}}
            <HdsTextDisplay
              @size="500"
              @tag="h1"
              {{boxMargin "0 0 md 0"}}
              data-test-page-global-error-title
            >
              {{t option.title}}
            </HdsTextDisplay>
          </header>
          <Flex @direction="column" @gap="sm">
            {{#if option.message}}
              <HdsTextBody @tag="p" data-test-page-global-error-message>
                {{t option.message}}
              </HdsTextBody>
            {{/if}}
            {{#if option.instructions}}
              <ul
                class="global-error-frame__instructions"
                data-test-page-global-error-instructions
              >
                {{#each option.instructions as |instruction|}}
                  <li>
                    <Typography
                      @component="span"
                      class="global-error-frame__instructions-message"
                    >
                      {{t instruction.message}}
                    </Typography>
                    <Typography
                      @component="span"
                      class="global-error-frame__instructions-help"
                    >
                      {{t instruction.help}}
                    </Typography>
                  </li>
                {{/each}}
              </ul>
            {{/if}}
            {{#if option.action}}
              <HdsTextBody @tag="p" data-test-page-global-error-action>
                {{t option.action}}
              </HdsTextBody>
            {{/if}}
            <HdsButtonSet>
              <HdsButton
                @color="tertiary"
                @icon="arrow-right"
                @text={{t "auth.ctas.retry"}}
                {{on "click" this.restartSession}}
                data-test-page-global-error-sign-out
              />
            </HdsButtonSet>
          </Flex>
        {{/let}}
        {{#if @error}}
          <HdsAccordion
            {{boxMargin "sm 0"}}
            data-test-page-global-error-details
            as |A|
          >
            <A.Item @containsInteractive={{true}}>
              <:toggle>
                <HdsTextBody>
                  {{t
                    "app.error-details"
                    supportLink="https://support.hashicorp.com/hc/en-us/requests/new"
                    htmlSafe=true
                  }}
                </HdsTextBody>
              </:toggle>
              <:content>
                <HdsCodeBlock
                  @value={{this.errorText}}
                  @language="javascript"
                  @hasLineNumbers={{false}}
                  @hasCopyButton={{true}}
                />
              </:content>
            </A.Item>
          </HdsAccordion>
        {{/if}}
      </:content>
    </GlobalErrorFrame>
  </template>
}
